import React, { useState } from 'react';
import { IconGift, IconCopy, IconCheck, IconStar } from '@tabler/icons-react';
import BaseModal from './BaseModal';

const FirstTimeCouponModal = ({ 
  isOpen, 
  onClose, 
  coupons = [], 
  userEligible = false,
  onCouponCopy
}) => {
  const [copiedCode, setCopiedCode] = useState(null);

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      onCouponCopy?.(code);
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `$${coupon.discount_value} OFF`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    return 'SPECIAL OFFER';
  };

  const formatExpiry = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    });
  };

  if (!userEligible || !coupons.length) return null;

  return (
    <BaseModal 
      isOpen={isOpen} 
      onClose={onClose}
      className="max-w-lg"
      closeOnOverlayClick={false}
    >
      <div className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-[#FFD500] to-[#FFA500] rounded-full flex items-center justify-center mx-auto mb-4">
            <IconGift size={32} className="text-[#0B0C52]" />
          </div>
          <h2 className="text-2xl font-bold text-[#0B0C52] mb-2">
            🎉 Welcome to Our Store!
          </h2>
          <p className="text-gray-600">
            As a first-time customer, you get exclusive deals just for you!
          </p>
        </div>

        {/* Coupons List */}
        <div className="space-y-4 mb-6">
          {coupons.map((coupon, index) => (
            <div 
              key={coupon.id}
              className="border-2 border-dashed border-[#FFD500] rounded-lg p-4 bg-gradient-to-r from-[#FFD500]/10 to-[#FFA500]/10 relative overflow-hidden"
            >
              {/* Best Deal Badge */}
              {index === 0 && (
                <div className="absolute top-0 right-0 bg-[#0B0C52] text-white text-xs px-2 py-1 rounded-bl-lg">
                  <IconStar size={12} className="inline mr-1" />
                  BEST DEAL
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-bold text-lg text-[#0B0C52] mb-1">
                    {formatDiscount(coupon)}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    {coupon.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {coupon.minimum_order_amount > 0 && (
                      <span>Min. order: ${coupon.minimum_order_amount} • </span>
                    )}
                    Expires: {formatExpiry(coupon.end_date)}
                  </div>
                </div>

                <div className="ml-4 text-center">
                  <div className="font-mono font-bold text-[#0B0C52] mb-2 text-lg border-2 border-dashed border-[#0B0C52] px-3 py-1 rounded">
                    {coupon.code}
                  </div>
                  <button
                    onClick={() => handleCopyCode(coupon.code)}
                    className={`flex items-center gap-1 text-xs px-3 py-1 rounded transition-all ${
                      copiedCode === coupon.code
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#0B0C52] text-white hover:bg-[#002080]'
                    }`}
                  >
                    {copiedCode === coupon.code ? (
                      <>
                        <IconCheck size={12} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <IconCopy size={12} />
                        Copy Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={() => {
              onClose();
              // Navigate to shopping or close modal
            }}
            className="flex-1 bg-[#FFD500] text-[#0B0C52] py-3 rounded-lg font-semibold hover:bg-[#FFCA00] transition-colors"
          >
            Start Shopping
          </button>
        </div>

        {/* Small Print */}
        <p className="text-xs text-gray-500 text-center mt-4">
          These exclusive offers are valid for first-time customers only. 
          Terms and conditions apply.
        </p>
      </div>
    </BaseModal>
  );
};

export default FirstTimeCouponModal;